/**
 * Example Strategies Documentation - QSL Format
 */
import type { DocPage } from '../types';

export const examplesDocs: DocPage[] = [
  {
    id: 'examples/minimal-strategy',
    title: 'Minimal Strategy',
    category: 'examples',
    tags: ['example', 'minimal', 'basic', 'starter', 'template', 'qsl'],
    version: '2.0',
    related: ['getting-started/overview', 'api/lifecycle/onBar'],
    order: 1,
    description: 'Smallest complete working strategy',
    content: `# Minimal Strategy

The smallest complete strategy that compiles and runs.

## Code

\`\`\`javascript
/**
 * Minimal RSI Strategy
 * Buys when RSI oversold, sells when overbought
 */

function define(ctx) {
  ctx.param('rsiPeriod', { 
    type: 'int', 
    label: 'RSI Period',
    default: 14, 
    min: 2, 
    max: 50 
  });
  ctx.param('oversold', { 
    type: 'int', 
    label: 'Oversold Level',
    default: 30, 
    min: 10, 
    max: 40 
  });
  ctx.param('overbought', { 
    type: 'int', 
    label: 'Overbought Level',
    default: 70, 
    min: 60, 
    max: 90 
  });
}

function init(ctx) {
  ctx.indicator('rsi', 'RSI', { period: ctx.p.rsiPeriod });
}

function onBar(ctx, i) {
  const rsi = ctx.ind.rsi[i];
  
  // Skip if indicator not ready
  if (q.isNaN(rsi)) return;
  
  const pos = ctx.position('ASSET');
  
  // Buy when RSI oversold
  if (rsi < ctx.p.oversold && pos.qty === 0) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'rsi_oversold' });
  }
  
  // Sell when RSI overbought
  if (rsi > ctx.p.overbought && pos.qty > 0) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'rsi_overbought' });
  }
}
\`\`\`

## What This Strategy Does

1. Declares RSI indicator in \`init()\`
2. Checks RSI value each bar in \`onBar()\`
3. Buys when RSI drops below oversold level
4. Sells when RSI rises above overbought level

## Key Concepts Demonstrated

- **Three functions**: \`define()\`, \`init()\`, \`onBar()\`
- **Parameters**: \`ctx.param()\` with type, bounds, labels
- **Indicators**: Declared in \`init()\`, pre-computed by engine
- **Position check**: \`ctx.position('ASSET').qty\`
- **Orders**: \`ctx.order.market()\`, \`ctx.order.close()\`
- **Helpers**: \`q.isNaN()\` for indicator warm-up

## Anatomy of a QSL Strategy

\`\`\`javascript
// 1. DEFINE: Declare parameters
function define(ctx) {
  ctx.param('name', { type: 'int', default: 14 });
}

// 2. INIT: Declare indicators and state
function init(ctx) {
  ctx.indicator('myIndicator', 'EMA', { period: ctx.p.name });
  ctx.state.tradeCount = 0;
}

// 3. ONBAR: Trading logic
function onBar(ctx, i) {
  // Your logic here
}
\`\`\`

## Related

- [EMA Crossover](/docs/examples/ema-crossover) — Trend-following example
- [Strategy Lifecycle](/docs/getting-started/strategy-lifecycle) — Execution order
`,
  },
  {
    id: 'examples/ema-crossover',
    title: 'EMA Crossover',
    category: 'examples',
    tags: ['example', 'ema', 'crossover', 'trend', 'moving average', 'qsl'],
    version: '2.0',
    related: ['examples/minimal-strategy', 'api/lifecycle/init'],
    order: 2,
    description: 'Classic trend-following strategy with EMA crossover',
    content: `# EMA Crossover Strategy

A classic trend-following strategy that trades EMA crossovers.

## Code

\`\`\`javascript
/**
 * EMA Crossover Strategy
 * 
 * Buys on bullish crossover (fast EMA crosses above slow EMA)
 * Sells on bearish crossover (fast EMA crosses below slow EMA)
 */

function define(ctx) {
  ctx.param('fastLength', {
    type: 'int',
    label: 'Fast EMA Period',
    default: 9,
    min: 5,
    max: 50,
    optimize: true,
    group: 'Trend'
  });
  
  ctx.param('slowLength', {
    type: 'int',
    label: 'Slow EMA Period',
    default: 21,
    min: 10,
    max: 200,
    optimize: true,
    group: 'Trend'
  });
}

function init(ctx) {
  ctx.indicator('fastEma', 'EMA', { period: ctx.p.fastLength, source: 'close' });
  ctx.indicator('slowEma', 'EMA', { period: ctx.p.slowLength, source: 'close' });
}

function onBar(ctx, i) {
  const fast = ctx.ind.fastEma;
  const slow = ctx.ind.slowEma;
  
  // Skip until indicators are ready
  if (q.isNaN(fast[i]) || q.isNaN(slow[i])) return;
  
  const pos = ctx.position('ASSET');
  
  // Bullish crossover: buy
  if (q.crossOver(fast, slow, i) && pos.qty <= 0) {
    if (pos.qty < 0) {
      ctx.order.close('ASSET', { signal: 'cover' });
    }
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'ema_crossover' });
  }
  
  // Bearish crossover: sell
  if (q.crossUnder(fast, slow, i) && pos.qty >= 0) {
    if (pos.qty > 0) {
      ctx.order.close('ASSET', { signal: 'sell' });
    }
    ctx.order.market('ASSET', -1, { signal: 'short', reason: 'ema_crossunder' });
  }
}
\`\`\`

## Key Concepts

### Using q.crossOver() / q.crossUnder()

The \`q\` namespace provides crossover detection:

\`\`\`javascript
// Bullish: fast crosses above slow
if (q.crossOver(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
  ctx.order.market('ASSET', 1, { signal: 'buy' });
}

// Bearish: fast crosses below slow
if (q.crossUnder(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
  ctx.order.close('ASSET', { signal: 'sell' });
}
\`\`\`

### Optimization-Ready Parameters

The \`optimize: true\` flag marks parameters for optimization:

\`\`\`javascript
ctx.param('fastLength', {
  type: 'int',
  default: 9,
  min: 5,
  max: 50,
  optimize: true  // Will be included in optimization runs
});
\`\`\`

### Parameter Groups

Group related parameters in the UI:

\`\`\`javascript
ctx.param('fastLength', { ..., group: 'Trend' });
ctx.param('slowLength', { ..., group: 'Trend' });
ctx.param('stopLoss', { ..., group: 'Risk' });
\`\`\`

## Long-Only Variant

If you only want to go long (no shorting):

\`\`\`javascript
function onBar(ctx, i) {
  const fast = ctx.ind.fastEma;
  const slow = ctx.ind.slowEma;
  
  if (q.isNaN(fast[i]) || q.isNaN(slow[i])) return;
  
  const pos = ctx.position('ASSET');
  
  // Buy on bullish cross
  if (q.crossOver(fast, slow, i) && pos.qty === 0) {
    ctx.order.market('ASSET', 1, { signal: 'buy' });
  }
  
  // Sell on bearish cross (no shorting)
  if (q.crossUnder(fast, slow, i) && pos.qty > 0) {
    ctx.order.close('ASSET', { signal: 'sell' });
  }
}
\`\`\`

## Performance Notes

- Works best in trending markets
- Whipsaws in ranging markets
- Consider adding filters (ATR, ADX, volume) for robustness

## Related

- [RSI Mean Reversion](/docs/examples/rsi-mean-reversion) — Counter-trend example
- [Helper Functions](/docs/api/helpers/overview) — q.crossOver(), q.crossUnder()
`,
  },
  {
    id: 'examples/rsi-mean-reversion',
    title: 'RSI Mean Reversion',
    category: 'examples',
    tags: ['example', 'rsi', 'mean reversion', 'momentum', 'qsl'],
    version: '2.0',
    related: ['examples/ema-crossover', 'api/helpers/overview'],
    order: 3,
    description: 'Counter-trend strategy using RSI extremes',
    content: `# RSI Mean Reversion Strategy

A counter-trend strategy that buys oversold conditions and sells overbought.

## Code

\`\`\`javascript
/**
 * RSI Mean Reversion Strategy
 * 
 * Buys when RSI exits oversold zone
 * Sells when RSI enters overbought zone
 */

function define(ctx) {
  ctx.param('rsiPeriod', {
    type: 'int',
    label: 'RSI Period',
    default: 14,
    min: 2,
    max: 50,
    optimize: true
  });
  
  ctx.param('oversold', {
    type: 'int',
    label: 'Oversold Level',
    default: 30,
    min: 10,
    max: 40
  });
  
  ctx.param('overbought', {
    type: 'int',
    label: 'Overbought Level',
    default: 70,
    min: 60,
    max: 90
  });
}

function init(ctx) {
  ctx.indicator('rsi', 'RSI', { period: ctx.p.rsiPeriod });
}

function onBar(ctx, i) {
  const rsi = ctx.ind.rsi;
  const { oversold, overbought } = ctx.p;
  
  if (q.isNaN(rsi[i]) || i < 1) return;
  
  const pos = ctx.position('ASSET');
  
  // Buy: RSI crosses above oversold level (exiting oversold)
  if (rsi[i - 1] <= oversold && rsi[i] > oversold && pos.qty === 0) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'rsi_exits_oversold' });
  }
  
  // Sell: RSI enters overbought zone
  if (rsi[i - 1] < overbought && rsi[i] >= overbought && pos.qty > 0) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'rsi_overbought' });
  }
}
\`\`\`

## Key Concepts

### Level Crossing vs Threshold

This strategy uses **level crossing** (not just threshold):

\`\`\`javascript
// LEVEL CROSSING: RSI was below, now above
if (rsi[i - 1] <= oversold && rsi[i] > oversold) {
  // Entering means momentum is shifting
}

// vs SIMPLE THRESHOLD (not used here)
if (rsi[i] < oversold) {
  // Could be oversold for many bars
}
\`\`\`

### Accessing Previous Bar

Use \`i - 1\` to check the previous bar's value:

\`\`\`javascript
if (i < 1) return; // Need at least 2 bars

const prevRsi = rsi[i - 1];
const currRsi = rsi[i];
\`\`\`

### Stop Loss Protection

Add a stop loss for risk management:

\`\`\`javascript
function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  
  if (pos.qty > 0) {
    const pnl = (ctx.series.close[i] - pos.avgPrice) / pos.avgPrice;
    
    // Stop loss at -3%
    if (pnl < -0.03) {
      ctx.order.close('ASSET', { signal: 'stop_loss' });
      return;
    }
  }
  
  // ... rest of logic
}
\`\`\`

## Variation: Exit on Opposite Signal

Instead of waiting for overbought, exit when RSI returns to neutral:

\`\`\`javascript
// Exit when RSI returns above 50 (neutral)
if (rsi[i] > 50 && pos.qty > 0) {
  ctx.order.close('ASSET', { signal: 'sell', reason: 'rsi_neutral' });
}
\`\`\`

## Performance Notes

- Works best in ranging/choppy markets
- Gets crushed in strong trends
- Consider adding trend filter (only trade with trend)

## Related

- [EMA Crossover](/docs/examples/ema-crossover) — Trend-following example
- [Helper Functions](/docs/api/helpers/overview) — Utility functions
`,
  },
  {
    id: 'examples/multi-indicator',
    title: 'Multi-Indicator Strategy',
    category: 'examples',
    tags: ['example', 'advanced', 'ema', 'rsi', 'atr', 'filter', 'qsl'],
    version: '2.0',
    related: ['examples/ema-crossover', 'api/lifecycle/init'],
    order: 4,
    description: 'Combining multiple indicators for robust signals',
    content: `# Multi-Indicator Strategy

A more robust strategy combining trend, momentum, and volatility filters.

## Code

\`\`\`javascript
/**
 * Multi-Indicator Strategy
 * 
 * Combines:
 * - EMA for trend direction
 * - RSI for momentum filter
 * - ATR for volatility-based stops
 */

function define(ctx) {
  // Trend
  ctx.param('emaLength', {
    type: 'int',
    label: 'EMA Length',
    default: 50,
    min: 20,
    max: 200,
    group: 'Trend'
  });
  
  // Momentum
  ctx.param('rsiPeriod', {
    type: 'int',
    label: 'RSI Period',
    default: 14,
    min: 2,
    max: 30,
    group: 'Momentum'
  });
  ctx.param('rsiLow', {
    type: 'int',
    label: 'RSI Buy Zone',
    default: 40,
    min: 20,
    max: 50,
    group: 'Momentum'
  });
  
  // Risk
  ctx.param('atrPeriod', {
    type: 'int',
    label: 'ATR Period',
    default: 14,
    min: 5,
    max: 30,
    group: 'Risk'
  });
  ctx.param('atrMultiplier', {
    type: 'float',
    label: 'ATR Stop Multiplier',
    default: 2.0,
    min: 1.0,
    max: 4.0,
    step: 0.5,
    group: 'Risk'
  });
}

function init(ctx) {
  ctx.indicator('ema', 'EMA', { period: ctx.p.emaLength, source: 'close' });
  ctx.indicator('rsi', 'RSI', { period: ctx.p.rsiPeriod });
  ctx.indicator('atr', 'ATR', { period: ctx.p.atrPeriod });
  
  ctx.state.entryPrice = null;
  ctx.state.stopPrice = null;
}

function onBar(ctx, i) {
  const ema = ctx.ind.ema[i];
  const rsi = ctx.ind.rsi[i];
  const atr = ctx.ind.atr[i];
  const close = ctx.series.close[i];
  
  // Wait for all indicators
  if (q.isNaN(ema) || q.isNaN(rsi) || q.isNaN(atr)) return;
  
  const pos = ctx.position('ASSET');
  
  // ═══════════════════════════════════════════
  // ENTRY LOGIC
  // ═══════════════════════════════════════════
  if (pos.qty === 0) {
    // Trend filter: price above EMA
    const trendUp = close > ema;
    
    // Momentum filter: RSI not overbought
    const momentumOk = rsi < ctx.p.rsiLow;
    
    // EMA rising
    const emaTrending = q.rising(ctx.ind.ema, i);
    
    if (trendUp && momentumOk && emaTrending) {
      ctx.order.market('ASSET', 1, { 
        signal: 'buy', 
        reason: 'trend_momentum_aligned' 
      });
      
      ctx.state.entryPrice = close;
      ctx.state.stopPrice = close - (atr * ctx.p.atrMultiplier);
    }
  }
  
  // ═══════════════════════════════════════════
  // EXIT LOGIC
  // ═══════════════════════════════════════════
  if (pos.qty > 0) {
    // Update trailing stop (only moves up)
    const newStop = close - (atr * ctx.p.atrMultiplier);
    if (newStop > ctx.state.stopPrice) {
      ctx.state.stopPrice = newStop;
    }
    
    // Check stop
    if (close < ctx.state.stopPrice) {
      ctx.order.close('ASSET', { signal: 'stop_loss', reason: 'atr_trailing_stop' });
      return;
    }
    
    // Trend reversal exit
    if (close < ema) {
      ctx.order.close('ASSET', { signal: 'sell', reason: 'trend_reversal' });
    }
  }
}
\`\`\`

## Key Concepts

### Multiple Filter Approach

Combine filters for higher-quality signals:

\`\`\`javascript
const trendUp = close > ema;           // Trend filter
const momentumOk = rsi < 40;           // Not overbought
const emaTrending = q.rising(ema, i);  // Trend strength

if (trendUp && momentumOk && emaTrending) {
  // All conditions aligned
}
\`\`\`

### ATR-Based Stops

Use ATR for volatility-adjusted stops:

\`\`\`javascript
// Stop = current price - (ATR × multiplier)
ctx.state.stopPrice = close - (atr * ctx.p.atrMultiplier);
\`\`\`

### Trailing Stop Logic

Only move stop in favorable direction:

\`\`\`javascript
const newStop = close - (atr * multiplier);
if (newStop > ctx.state.stopPrice) {
  ctx.state.stopPrice = newStop;  // Only ratchet up
}
\`\`\`

## Variations

### Add Volume Filter

\`\`\`javascript
function init(ctx) {
  // ... existing indicators
  ctx.state.avgVolume = null;
}

function onBar(ctx, i) {
  const volume = ctx.series.volume[i];
  const avgVolume = q.avg(ctx.series.volume, 20, i);
  
  // Only trade on above-average volume
  const volumeOk = volume > avgVolume * 1.2;
  
  if (trendUp && momentumOk && volumeOk) {
    // Entry
  }
}
\`\`\`

### Add Time Filter

\`\`\`javascript
function onBar(ctx, i) {
  // Don't enter in last 10 bars
  const totalBars = ctx.series.close.length;
  if (i > totalBars - 10) return;
  
  // ... rest of logic
}
\`\`\`

## Related

- [Helper Functions](/docs/api/helpers/overview) — q.rising(), q.avg()
- [ctx.state](/docs/api/lifecycle/init) — Managing state
`,
  },
  {
    id: 'examples/tdr-strategy',
    title: 'TDR Strategy (Trend Direction & Risk)',
    category: 'examples',
    tags: ['example', 'advanced', 'tdr', 'donchian', 'ppo', 'risk', 'trend', 'partial', 'qsl'],
    version: '2.0',
    related: ['api/indicators/donchian', 'api/indicators/ppo', 'examples/multi-indicator'],
    order: 5,
    description: 'Advanced strategy with multiple signal types: preEntry, lowRiskEntry, takeProfit, highRiskTP',
    content: `# TDR Strategy (Trend Direction & Risk)

A sophisticated strategy combining trend detection with risk-based position management.

## Overview

TDR generates multiple signal types for nuanced position management:

| Signal | Type | Trigger |
|--------|------|---------|
| **buySignal** | Full Entry | EMA crosses above Donchian baseline |
| **sellSignal** | Full Exit | EMA crosses below Donchian baseline |
| **preEntry** | Partial Entry | Price crosses short baseline (early positioning) |
| **takeProfit** | Partial Exit | Price crosses below short baseline in profit |
| **lowRiskEntry** | Partial Entry | Extended time in low-risk PPO zone |
| **highRiskTP** | Partial Exit | Extended time in high-risk PPO zone |

## Key Concepts

### Donchian Channel Baseline

The Donchian channel provides a dynamic baseline (midpoint of highest high and lowest low):

\`\`\`javascript
ctx.indicator('baseline', 'DONCHIAN', { period: 50 });

// Access the middle line (baseline)
const baseline = ctx.ind.baseline_middle;

// EMA crossing above/below baseline defines trend
if (q.crossOver(ema, baseline, i)) {
  // Bullish trend confirmed
}
\`\`\`

### PPO Risk Zones

The PPO (Percent Price Oscillator) measures momentum on a -100 to +100 scale:

\`\`\`javascript
ctx.indicator('ppo', 'PPO', { 
  shortGamma: 0.4,   // Fast Laguerre filter
  longGamma: 0.8,    // Slow Laguerre filter
  accuracy: 10       // Calculation precision
});

// Define risk zones
const ppoValue = ctx.ind.ppo[i];
if (ppoValue < -85) {
  // Low risk zone: good for entries
} else if (ppoValue > 85) {
  // High risk zone: consider taking profits
}
\`\`\`

### Zone Duration Signals

LowRiskEntry and HighRiskTP fire when the market stays in a risk zone for an extended period (average zone length):

\`\`\`javascript
function checkLowRiskEntry(riskState) {
  // Fire when current zone length equals average
  if (riskState.inLowRisk && riskState.avgLowRiskLength > 0) {
    return riskState.lowRiskLength === Math.round(riskState.avgLowRiskLength);
  }
  return false;
}
\`\`\`

### Trading Modes

TDR supports all three trading modes: **Long-only**, **Short-only**, and **Bidirectional**. The mode is accessed via \`ctx.p.tradingMode\` and the strategy branches its order logic accordingly. This is what allows TDR to be used in any direction from the UI.

For a complete guide on implementing trading modes in your own strategies, including code examples for each mode, partial positions, and common mistakes, see the **Trading Modes** guide under Best Practices.

## Signal Delay Pattern

TDR uses 1-bar delayed execution for realistic backtesting:

\`\`\`javascript
// Store raw signal for next bar
ctx.state.delayed.prevBuy = rawBuySignal;

// Execute delayed signal from previous bar
const buySignal = ctx.state.delayed.prevBuy;
if (buySignal) {
  ctx.order.market('ASSET', 1, { signal: 'buy' });
}
\`\`\`

## Partial Position Sizing

Use partial entries and exits for sophisticated position management:

\`\`\`javascript
// Partial entry (5% of portfolio)
if (preEntry && ctx.state.tradeState === 'flat') {
  ctx.order.market('ASSET', 0.05, { signal: 'preEntry' });
}

// Partial exit (reduce position by 5%)
if (highRiskTP && ctx.state.tradeState === 'long') {
  ctx.order.reduce('ASSET', 0.05, { signal: 'highRiskTP' });
}
\`\`\`

## Performance Notes

- Best on 4h and daily timeframes
- Strong in trending markets with pullbacks
- PPO zones help avoid chasing extended moves
- Partial signals allow gradual position building/reduction

## Related

- [Donchian Indicator](/docs/api/indicators/donchian) - Channel calculation details
- [PPO Indicator](/docs/api/indicators/ppo) - Risk oscillator details
- [Multi-Indicator Strategy](/docs/examples/multi-indicator) - Simpler multi-indicator example
`,
  },

  // Bollinger Breakout
  {
    id: 'examples/bollinger-breakout',
    title: 'Bollinger Breakout',
    category: 'examples',
    tags: ['example', 'bollinger', 'bands', 'breakout', 'volatility', 'mean reversion', 'qsl'],
    version: '2.0',
    related: ['api/indicators/bbands', 'examples/rsi-mean-reversion', 'examples/multi-indicator'],
    order: 6,
    description: 'Volatility breakout strategy using Bollinger Bands',
    content: `# Bollinger Breakout Strategy

A volatility breakout strategy that enters when price expands beyond the upper Bollinger Band and exits on mean reversion back to the middle band.

## Concept

Bollinger Bands measure volatility. When price pushes through the upper band, it signals strong momentum. The strategy rides this momentum until price reverts to the moving average (middle band).

| Signal | Condition | Logic |
|--------|-----------|-------|
| **Buy** | Close > Upper Band | Volatility breakout |
| **Sell** | Close < Middle Band | Mean reversion |

## Code

\`\`\`javascript
/**
 * Bollinger Breakout Strategy
 * 
 * Buy: Price closes above upper band (breakout)
 * Sell: Price closes below middle band (mean reversion)
 */

function define(ctx) {
  ctx.param('period', {
    type: 'int',
    label: 'BB Period',
    default: 20,
    min: 10,
    max: 50,
    step: 5,
    optimize: true,
  });
  
  ctx.param('stdDev', {
    type: 'float',
    label: 'Std Dev Multiplier',
    default: 2.0,
    min: 1.0,
    max: 3.0,
    step: 0.5,
    optimize: true,
  });
}

function init(ctx) {
  ctx.addIndicator('bb', 'BB', {
    period: ctx.p.period,
    stdDev: ctx.p.stdDev,
    source: 'close',
  });
}

function onBar(ctx, i) {
  const upper = ctx.ind.bb_upper[i];
  const middle = ctx.ind.bb_middle[i];
  const close = ctx.series.close[i];
  
  // Skip if indicators not ready
  if (q.isNaN(upper) || q.isNaN(middle)) return;
  
  const pos = ctx.position('ASSET');
  
  // Buy: breakout above upper band
  if (close > upper && pos.qty === 0) {
    ctx.order.market('ASSET', 1, { 
      signal: 'buy', 
      reason: 'upper_band_breakout' 
    });
  }
  
  // Sell: mean reversion back to middle band
  if (close < middle && pos.qty > 0) {
    ctx.order.close('ASSET', { 
      signal: 'sell', 
      reason: 'mean_reversion' 
    });
  }
}
\`\`\`

## How It Works

### Bollinger Bands Output

The \`BB\` indicator produces three series:

| Series | Access | Description |
|--------|--------|-------------|
| Upper | \`ctx.ind.bb_upper[i]\` | Middle + (stdDev * standard deviation) |
| Middle | \`ctx.ind.bb_middle[i]\` | Simple moving average of close |
| Lower | \`ctx.ind.bb_lower[i]\` | Middle - (stdDev * standard deviation) |

### Entry Logic

When the close price exceeds the upper band, it means price has moved more than N standard deviations above the mean — a statistically significant move that often continues.

### Exit Logic

The middle band (SMA) acts as a "fair value" reference. When price drops back below it, the breakout momentum has faded.

## Parameters

| Parameter | Default | Range | Purpose |
|-----------|---------|-------|---------|
| \`period\` | 20 | 10-50 | Lookback for SMA and standard deviation |
| \`stdDev\` | 2.0 | 1.0-3.0 | Band width in standard deviations |

**period:** Shorter periods (10-15) create tighter, more reactive bands. Longer periods (30-50) create wider bands that filter out noise.

**stdDev:** Higher values (2.5-3.0) require stronger breakouts, producing fewer but higher-quality signals. Lower values (1.0-1.5) generate more signals with more false breakouts.

## Best Markets

- Works well in markets that alternate between **consolidation and expansion**
- Performs in crypto due to frequent volatility cycles
- Struggles in steadily trending markets with no clear breakout points

## Recommended Timeframes

| Timeframe | Character |
|-----------|-----------|
| 4h | Good balance of signal frequency and reliability |
| 1d | Fewer signals, higher quality breakouts |
| 1h | More signals, more false breakouts — consider adding volume filters |

## Enhancement Ideas

- Add volume confirmation (only enter breakouts with above-average volume)
- Detect band squeeze (narrowing bands) before entry for higher probability setups
- Use a trailing stop instead of mean reversion exit for stronger trends
- Add RSI filter to avoid buying into already-extended momentum

## Related

- [Bollinger Bands Indicator](/docs/api/indicators/bbands) — Indicator details
- [RSI Mean Reversion](/docs/examples/rsi-mean-reversion) — Complementary approach
- [Multi-Indicator](/docs/examples/multi-indicator) — Combining multiple indicators
`,
  },
];
